import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { Logger, LoggingService } from 'ionic-logging-service';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { EnvironmentService } from '../environment/environment.service';
import { EventService } from '../events/event.service';
import { RETURN_URL_KEY } from '../return-url/return-url.service';
import { StorageService } from '../storage/storage.service';
import { TenantService, TENANT_LIST_LENGTH_KEY } from '../tenant/tenant.service';
import { UserService } from '../user/user.service';
import { Observable, of } from 'rxjs';

/**
 * Why a sign-out is happening. The two callers want different things, and the difference is
 * not cosmetic — see {@link LogoutService.signOutUser}.
 *
 * 'user-initiated' — the user asked to leave (menu, AuthPage sign-out button).
 * 'session-ended'  — a 401/403 signed them out for us, and they are about to sign back in.
 */
export type SignOutIntent = 'user-initiated' | 'session-ended';

@Injectable({
  providedIn: 'root'
})
export class LogoutService {

  private logger: Logger;
  private storedItemKeys = ['lastUsedTenant', 'idpLoginToken', TENANT_LIST_LENGTH_KEY];

  constructor(
    private authService: AuthService,
    private environmentService: EnvironmentService,
    private eventService: EventService,
    private httpClient: HttpClient,
    private loggingService: LoggingService,
    public navController: NavController,
    private storageService: StorageService,
    private tenantService: TenantService,
    private userService: UserService
  ) {
    this.logger = this.loggingService.getLogger("[LogoutService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  /**
   * Signs the user out: revokes tokens and clears local user/tenant/Pusher state.
   *
   * The intent decides one thing: whether the pending return URL goes with it. A user who chose
   * to leave should not have their route fire for whoever signs in next; a user ejected by a
   * 401/403 is about to sign back in, and that route is why it was stashed. Same split as
   * {@link endBrowserSsoSession}, which is also kept out of the 401/403 path.
   *
   * @param intent - Why the sign-out is happening
   * @returns Completes once the sign-out chain has run
   */
  public signOutUser(intent: SignOutIntent): Observable<void> {
    this.logger.info('signOutUser() intent: ' + intent);
    const keysToRemove = intent === 'user-initiated'
      ? [...this.storedItemKeys, RETURN_URL_KEY]
      : this.storedItemKeys;
    // Revoke the tokens server-side and clear token storage first. If revocation
    // fails (offline, server error, already-expired token) we still continue
    // clearing local user/tenant/Pusher state so the app ends up signed out.
    return this.authService.deAuthenticateUser().pipe(
      take(1),
      catchError(err => {
        this.logger.error('signOutUser() deAuthenticateUser error: ' + JSON.stringify(err));
        return of(void 0);
      }),
      switchMap(() => this.storageService.removeStoredItems(keysToRemove)),
      switchMap(() => this.userService.resetUser()),
      switchMap(() => this.tenantService.resetTenant()),
      switchMap(() => this.eventService.resetPusher()),
      catchError(err => {
        this.logger.error('signOutUser() chain error: ' + JSON.stringify(err));
        return of(err); // or throwError(err) if you want to propagate
      }),
      map(() => void 0),
    );
  }

  /**
   * Signs out a rejected session and then recovers, whether or not the sign-out succeeded.
   *
   * Both auth pages do this when the tenant list is refused, differing only in what recovery
   * means. Shared because the parts that are the same are the parts that matter: the
   * 'session-ended' intent, and recovering whatever the sign-out did.
   *
   * No error handler, deliberately: signOutUser() swallows its own failures in its trailing
   * catchError, so this observable never errors and a handler here would be unreachable.
   *
   * @param recover - What the caller does once the sign-out has been attempted
   */
  public signOutAndRecover(recover: () => void): void {
    this.signOutUser('session-ended').pipe(take(1)).subscribe(() => recover());
  }

  // Browser-only final sign-out step: POST ENDPOINT_SIGN_OUT_BROWSER so the auth
  // server clears the SSO session cookie. Called from AuthPage (not from
  // signOutUser) so the 401/403 auto sign-out path does not end the SSO session.
  // withCredentials sends the session cookie; responseType 'text' because /logout
  // responds with text rather than JSON. The server returns 200 OK (no redirect) —
  // the app itself routes on to sign-in afterwards. Failures are tolerated — the
  // caller still proceeds to the sign-in app.
  public endBrowserSsoSession(): Observable<void> {
    this.logger.info('endBrowserSsoSession()');
    const signOutUrl: string = this.environmentService.getEnvironmentEndpoint['ENDPOINT_SIGN_OUT_BROWSER'];
    return this.httpClient.post(signOutUrl, null, { withCredentials: true, responseType: 'text' }).pipe(
      take(1),
      catchError(err => {
        this.logger.error('endBrowserSsoSession() error: ' + JSON.stringify(err));
        return of(null);
      }),
      map(() => void 0),
    );
  }
}